import { useState } from 'react';
import { Loader2, Save } from 'lucide-react';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { cn } from '../../../lib/cn';
import { useNotifications } from '../../../store/useStore';
import {
  AMBITI,
  templateService,
  type AmbitoModello,
} from '../../../lib/email/templateService';
import type { DocumentoEmail } from '@email/types.ts';

interface SalvaModelloDialogProps {
  aperto: boolean;
  onChiudi: (aperto: boolean) => void;
  /** Quello che si sta mettendo da parte. */
  documento: DocumentoEmail;
  oggetto: string;
  preheader?: string | null;
  /** Nome proposto: di solito quello della campagna o della casella. */
  nomeProposto?: string;
  ambitoProposto?: AmbitoModello;
}

/**
 * Mette da parte la email che si sta componendo, per ripartire da li' la
 * prossima volta.
 *
 * Quello che si salva e' una copia: ritoccare il modello domani non cambia la
 * campagna nata da lui ieri.
 */
export function SalvaModelloDialog({
  aperto,
  onChiudi,
  documento,
  oggetto,
  preheader,
  nomeProposto,
  ambitoProposto = 'ovunque',
}: SalvaModelloDialogProps) {
  const { addNotification } = useNotifications();

  const [nome, setNome] = useState(nomeProposto ?? '');
  const [descrizione, setDescrizione] = useState('');
  const [ambito, setAmbito] = useState<AmbitoModello>(ambitoProposto);
  const [salvando, setSalvando] = useState(false);

  const vuota = (documento.blocchi?.length ?? 0) === 0;

  const salva = async () => {
    if (!nome.trim() || vuota) return;
    setSalvando(true);
    try {
      await templateService.crea({
        name: nome.trim(),
        description: descrizione.trim() || null,
        subject: oggetto,
        preheader: preheader ?? null,
        document: documento,
        ambito,
      });
      addNotification({
        type: 'success',
        title: 'Modello salvato',
        message: `"${nome.trim()}" si puo' riusare da adesso.`,
      });
      setNome('');
      setDescrizione('');
      onChiudi(false);
    } catch (errore) {
      const messaggio = errore instanceof Error ? errore.message : 'Errore imprevisto.';
      addNotification({
        type: 'error',
        title: 'Non riesco a salvare il modello',
        // Il nome e' unico: e' il caso che capita davvero, e detto cosi' si
        // capisce cosa fare.
        message: messaggio.includes('duplicate key')
          ? 'Esiste gia\' un modello con questo nome. Cambialo.'
          : messaggio,
      });
    } finally {
      setSalvando(false);
    }
  };

  return (
    <Dialog open={aperto} onOpenChange={onChiudi}>
      <DialogContent className="max-w-md flex flex-col overflow-y-hidden sm:max-h-[80vh]">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>Salva come modello</DialogTitle>
          <p className="text-xs text-neutral-500">
            Da qui in poi potrai ripartire da questa email invece di rifarla.
          </p>
        </DialogHeader>

        <DialogBody className="space-y-3 py-2">
          {vuota && (
            <p className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-lg p-2.5">
              La email e' vuota: non c'e' niente da mettere da parte.
            </p>
          )}

          <div>
            <Label htmlFor="nome-modello" className="text-xs">
              Come si chiama
            </Label>
            <Input
              id="nome-modello"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              placeholder="Benvenuto ai nuovi iscritti"
              className="mt-1"
            />
            <p className="text-[11px] text-neutral-400 mt-1">
              Serve a ritrovarlo: due modelli non possono chiamarsi uguale.
            </p>
          </div>

          <div>
            <Label htmlFor="descrizione-modello" className="text-xs">
              A cosa serve (facoltativo)
            </Label>
            <Textarea
              id="descrizione-modello"
              rows={2}
              value={descrizione}
              onChange={(e) => setDescrizione(e.target.value)}
              placeholder="Quando usarlo, cosa cambiare prima di mandarlo"
              className="mt-1 text-sm"
            />
          </div>

          <div>
            <Label className="text-xs">Dove proporlo</Label>
            <div className="space-y-1.5 mt-1.5">
              {(Object.keys(AMBITI) as AmbitoModello[]).map((a) => (
                <button
                  key={a}
                  type="button"
                  onClick={() => setAmbito(a)}
                  className={cn(
                    'w-full text-left px-3 py-2 rounded-lg border transition-colors',
                    ambito === a
                      ? 'border-primary-400 bg-primary-50/50'
                      : 'border-gray-200 hover:border-gray-300'
                  )}
                >
                  <span className="block text-xs font-semibold text-gray-900">
                    {AMBITI[a].label}
                  </span>
                  <span className="block text-[11px] text-neutral-400 mt-0.5 leading-relaxed">
                    {AMBITI[a].spiegazione}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </DialogBody>

        <DialogFooter className="flex-shrink-0">
          <Button variant="outline" onClick={() => onChiudi(false)} disabled={salvando}>
            Annulla
          </Button>
          <Button
            onClick={salva}
            disabled={!nome.trim() || vuota || salvando}
            className="bg-primary-600 hover:bg-primary-700 text-white"
          >
            {salvando ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Salva
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
